import { useState } from "react";
import { cn } from "./ui";

/* Столбчатый график отчёта: сетка по оси Y, стек по дням, подсказка при наведении
   и пустое состояние с той самой плашкой «фильтры не содержат подходящих данных». */

export type ChartTone = "ok" | "bad" | "neutral";

const FILL: Record<ChartTone, string> = {
  ok: "bg-emerald-500",
  bad: "bg-red-400",
  neutral: "bg-primary/70",
};

export const toneDot: Record<ChartTone, string> = {
  ok: "bg-emerald-500",
  bad: "bg-red-400",
  neutral: "bg-primary/70",
};

type Props = {
  labels: string[];
  fullLabels: string[];
  series: { label: string; tone: ChartTone; values: number[]; color?: string }[];
  empty: boolean;
  emptyText: string;
  height?: number;
};

export function ReportChart({ labels, fullLabels, series, empty, emptyText, height = 168 }: Props) {
  const [hover, setHover] = useState<number | null>(null);

  const totals = labels.map((_, i) => series.reduce((sum, s) => sum + (s.values[i] ?? 0), 0));
  const peak = Math.max(1, ...totals);
  const step = peak <= 1 ? 3 : Math.max(1, Math.ceil(peak / 4));
  const nice = step * 4;
  const ticks = [4, 3, 2, 1, 0].map((k) => k * step);
  const every = Math.max(1, Math.ceil(labels.length / 12));

  return (
    <div className="flex items-stretch gap-2">
      <div className="flex flex-none flex-col justify-between text-right text-[9.5px] tabular-nums text-muted-foreground" style={{ height }}>
        {ticks.map((t) => (
          <span key={t}>{t}</span>
        ))}
      </div>

      <div className="min-w-0 flex-1">
        <div className="relative" style={{ height }}>
          {/* сетка */}
          <div className="pointer-events-none absolute inset-0 flex flex-col justify-between" aria-hidden>
            {ticks.map((t) => (
              <span key={t} className="h-px w-full bg-border/70" />
            ))}
          </div>

          {/* столбцы */}
          <div className="absolute inset-0 flex items-end gap-[3px]">
            {labels.map((_, i) => {
              const total = totals[i];
              const active = hover === i;
              return (
                <div
                  key={i}
                  className="relative flex h-full flex-1 cursor-pointer flex-col justify-end"
                  onMouseEnter={() => setHover(i)}
                  onMouseLeave={() => setHover(null)}
                  onFocus={() => setHover(i)}
                  onBlur={() => setHover(null)}
                  tabIndex={0}
                  role="img"
                  aria-label={`${fullLabels[i]}: ${series.map((s) => `${s.label} ${s.values[i] ?? 0}`).join(", ")}`}
                >
                  {!empty && active ? (
                    <span className="pointer-events-none absolute bottom-full start-1/2 z-20 mb-1 w-max max-w-[11rem] -translate-x-1/2 rounded-btn border border-border bg-card px-2 py-1.5 text-[10px] leading-tight shadow-lift">
                      <span className="block font-semibold">{fullLabels[i]}</span>
                      {series.map((s) => (
                        <span key={s.label} className="mt-0.5 flex items-center gap-1 text-muted-foreground">
                          <span className={cn("size-1.5 rounded-full", s.color ?? toneDot[s.tone])} aria-hidden />
                          {s.label}: <b className="tabular-nums text-foreground">{s.values[i] ?? 0}</b>
                        </span>
                      ))}
                    </span>
                  ) : null}
                  <span
                    className={cn("flex w-full flex-col-reverse", active && "opacity-90")}
                    style={{ height: total > 0 ? `${Math.max((total / nice) * 100, 2)}%` : "0%" }}
                  >
                    {series.map((s, j) => (
                      <span
                        key={s.label}
                        className={cn("w-full", s.color ?? FILL[s.tone], j === series.length - 1 && "rounded-t-[2px]")}
                        style={{ flexGrow: s.values[i] ?? 0 }}
                      />
                    ))}
                  </span>
                </div>
              );
            })}
          </div>

          {empty ? (
            <div className="pointer-events-none absolute inset-0 z-10 grid place-items-center px-3">
              <p className="flex max-w-[22rem] items-center gap-1.5 rounded-btn border border-border bg-card px-3 py-2 text-center text-[10.5px] leading-relaxed text-muted-foreground shadow-sm">
                <i className="ri-error-warning-line flex-none text-[12px] leading-none" aria-hidden />
                {emptyText}
              </p>
            </div>
          ) : null}
        </div>

        <div className="mt-1.5 flex h-3.5 gap-[3px]">
          {labels.map((l, i) => (
            <span key={i} className="relative min-w-0 flex-1 text-center text-[9px] tabular-nums text-muted-foreground">
              {i % every === 0 ? <span className="absolute start-1/2 top-0 -translate-x-1/2 whitespace-nowrap">{l}</span> : null}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ReportChart;